import { Col, Menu, Row, Skeleton, Space } from "antd";
import { Menu as MenuIcon, BarChart3, Users, CreditCard, KeyRound, LockKeyhole } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { getProjectService, isApiKeysEnabled } from "../../backend";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import PageContentWrapper from "../../components/PageContentWrapper";
import { useCurrentProject } from "../../contexts/Project";
import { PageHeader } from "@ant-design/pro-layout";

export default function ProjectSettingsRoot() {
    const navigate = useNavigate();
    const location = useLocation();
    const { projectId } = useParams();
    const [activeTab, setActiveTab] = useState("general");
    const currentProject = useRecoilValue(currentProjectState);
    const { updateCurrentProject } = useCurrentProject();
    // The stored project can still be the previously opened one on a deep link; the
    // tabs read it, so they wait for it to match the URL instead of editing the wrong project
    const ready = !!projectId && currentProject?.id === projectId;

    useEffect(() => {
        if (projectId && currentProject?.id !== projectId) updateCurrentProject(projectId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, currentProject?.id]);

    useEffect(() => {
        setActiveTab(location.pathname.split("/")[4] || "general");

        const loadData = async () => {
            await getProjectService().getUsers(projectId!);
        }
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return (
        <PageContentWrapper
            size="large"
            header={
                <PageHeader
                    title="Project Settings"
                />
            }
        >
            <Row gutter={24}>
                <Col span={6}>
                    <Menu
                        mode="inline"
                        className="ant-card-bordered"
                        selectedKeys={[activeTab]}
                        onSelect={(item) => {
                            navigate(item.key);
                            setActiveTab(item.key);
                        }}
                        items={[
                            {
                                key: "general",
                                label: <Space>
                                    <MenuIcon size={16} style={{ display: 'flex', alignItems: 'center' }} />
                                    General
                                </Space>
                            },
                            {
                                key: "usage",
                                label: <Space>
                                    <BarChart3 size={16} style={{ display: 'flex', alignItems: 'center' }} />
                                    Plan usage
                                </Space>
                            },
                            {
                                key: "members",
                                label: <Space>
                                    <Users size={16} style={{ display: 'flex', alignItems: 'center' }} />
                                    Members
                                </Space>
                            },
                            {
                                key: "security",
                                label: <Space>
                                    <LockKeyhole size={16} style={{ display: 'flex', alignItems: 'center' }} />
                                    Security
                                </Space>
                            },
                            ...(isApiKeysEnabled(projectId!) ? [{
                                key: "api-keys",
                                label: <Space>
                                    <KeyRound size={16} style={{ display: 'flex', alignItems: 'center' }} />
                                    API keys
                                </Space>
                            }] : []),
                            {
                                key: "billing",
                                label: <Space>
                                    <CreditCard size={16} style={{ display: 'flex', alignItems: 'center' }} />
                                    Billing
                                </Space>
                            }
                        ]}
                    />
                </Col>
                <Col span={18}>
                    {ready
                        ? <Outlet key={projectId} context={{ projectDetails: currentProject }} />
                        : <Skeleton active paragraph={{ rows: 6 }} />}
                </Col>
            </Row >
        </PageContentWrapper>
    )
}
